import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  type SvgIconTypeMap,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import useParseURL from 'hooks/useParseURL';
import EditIcon from '@mui/icons-material/Edit';
import CopyIcon from '@mui/icons-material/FileCopy';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import OutputIcon from '@mui/icons-material/Output';
import {inputMenuActionType, methodType, FormSelectionEnum, SWRKeyEnum} from 'interfaces';
import backendCommunicator from 'services/backendCommunicator';
import FileDownload from 'js-file-download';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';

import {mutate} from 'swr';
import * as I from 'interfaces';

import {useState} from 'react';
import {useInputMenuContext} from 'context/InputMenu';
import {type Plan} from 'client';
import Alert from 'helpers/Alert';
import DeleteAlert from 'components/alerts/DeleteAlert';
import {EMPTY_ID} from 'hooks/swr';
import {type OverridableComponent} from '@mui/material/OverridableComponent';

import axios from 'axios';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useTranslation} from 'react-i18next';
import {getBaseUrl} from '../../../../utils/runtimeVars';
import {PlanSettingsModal} from './PlanSettingsModal';
import {useModalWindow} from 'hooks/useModalWindow';
import {PlanPublishModal} from 'components/InputMenu/PlanPublishModal';
import DownloadHarvestReportDialog from 'components/InputMenu/DownloadHarvestReportDialog';
import TimeseriesDownloadModal from 'components/InputMenu/TimeseriesDownloadModal';

export const PlanActionsMenu = ({plan}: {plan: Plan}) => {
  const {newPlanVersions, publishToExternalApi, harvestReportEnabled} = useFlags();
  const [anchorEl, setAnchorEl] = useState(null);
  const {t} = useTranslation('common');

  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {openModalWindow} = useModalWindow();
  const {navigate, urlSearchParams, planId} = useParseURL();
  const open = Boolean(anchorEl);
  const [openDeleteAlert, setOpenDeleteAlert] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<string>();
  const [openModal, setOpenModal] = useState<boolean>(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickExportPlan = (title?: string) => {
    const fileName = `${plan.name}.json`;
    setIsDownloading(title);
    backendCommunicator
      .exportPlan(planId)
      .then((response) => {
        FileDownload(JSON.stringify(response.data), fileName);
      })
      .catch((error) => {
        Alert.error(error);
      })
      .finally(() => {
        setIsDownloading(undefined);
        handleClose();
      });
  };

  async function handleClickCreateHarvestReport() {
    const payload = {name: plan.name, planId: plan.id};

    await backendCommunicator.createHarvestReport(payload).then((data) => {
      handleClose();
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append(I.URLParams.newId, data.data.id);
      void mutate([EMPTY_ID, SWRKeyEnum.GET_HARVEST_REPORTS]);
      navigate({
        pathname: I.URLPathname.harvestReports,
        search: urlSearchParams.toString(),
      });
    });
  }

  const handleClickDelete = () => {
    setOpenDeleteAlert(true);
    handleClose();
  };

  const redirectToHome = () => {
    urlSearchParams.delete(I.URLParams.plan);
    urlSearchParams.delete(I.URLParams.site);
    urlSearchParams.delete(I.URLParams.cage);
    navigate({
      pathname: I.URLPathname.root,
      search: urlSearchParams.toString(),
    });
  };

  const EXCEL_EXTENSION = '.xlsx';
  const BASE_URL = getBaseUrl('');
  const downloadConcessionAllocations = (title?: string) => {
    const _method = 'GET';
    setIsDownloading(title);
    axios
      .request({
        url: `${BASE_URL}/CageAllocationPerConcession?planId=${plan.id}`,
        method: _method,
        responseType: 'blob',
      })
      .then(({data}) => {
        FileDownload(
          data,
          `${plan.name.replace(/\s+/g, '_')}_${t('downloadConcessionAllocationsFileNameSuffix')}${EXCEL_EXTENSION}`,
        );
        handleClose();
        setIsDownloading(undefined);
      })
      .catch((error) => {
        Alert.error(error);
      });
  };

  function publishPlanHandler() {
    handleClose();
    openModalWindow(PlanPublishModal, {payload: {id: plan.id}});
  }

  const handleSetting = () => {
    setOpenModal(true);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('planActions') ?? ''}>
        <IconButton size="small" onClick={handleClick} data-testid="plan-actions-menu">
          <MoreVertIcon fontSize="small" color="secondary" />
        </IconButton>
      </Tooltip>

      {openDeleteAlert && (
        <DeleteAlert
          open={openDeleteAlert}
          setOpen={setOpenDeleteAlert}
          id={plan.id}
          type={FormSelectionEnum.plan}
          handleCommunicationSucces={() => {
            void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
            Alert.success(`${t('plan')} ${t('deleted')}`);
            redirectToHome();
          }}
        />
      )}
      {openModal && <PlanSettingsModal open={openModal} setOpen={setOpenModal} />}

      <Menu open={open} anchorEl={anchorEl} onClose={handleClose} sx={{zIndex: 3000}}>
        <AppBarMenuItem
          onClick={() => {
            handleClickCopy(planId, handleClose, inputMenuDispatch);
          }}
          Icon={CopyIcon}
          title={t('copy')}
        />
        <AppBarMenuItem
          onClick={() => {
            handleClickEdit(planId, handleClose, inputMenuDispatch);
          }}
          Icon={EditIcon}
          title={t('edit')}
        />
        <AppBarMenuItem onClick={handleClickDelete} Icon={DeleteIcon} title={t('delete')} />
        <Divider variant="middle" />
        <AppBarMenuItem
          onClick={() => {
            openModalWindow(TimeseriesDownloadModal);
            handleClose();
          }}
          Icon={DownloadIcon}
          title={t('downloadTimeSeriesReport')}
          testid={'timeseries-download-menu'}
        />
        <AppBarMenuItem
          onClick={downloadConcessionAllocations}
          Icon={DownloadIcon}
          title={t('downloadConcessionAllocations')}
          isDownloading={isDownloading}
        />
        {harvestReportEnabled && (
          <AppBarMenuItem
            onClick={() => {
              openModalWindow(DownloadHarvestReportDialog);
              handleClose();
            }}
            Icon={DownloadIcon}
            title={t('downloadHarvestReportMenuItemName')}
          />
        )}
        <AppBarMenuItem
          onClick={handleClickExportPlan}
          Icon={DownloadIcon}
          title={t('exportPlan')}
          isDownloading={isDownloading}
        />
        <Divider variant="middle" />
        <AppBarMenuItem
          onClick={() => {
            void handleClickCreateHarvestReport();
          }}
          Icon={DescriptionIcon}
          title={t('createHarvestReport')}
        />
        {newPlanVersions && (
          <AppBarMenuItem
            onClick={() => {
              const urlSearchParams = new URLSearchParams();
              urlSearchParams.append(I.URLParams.plan, planId);
              handleClose();
              navigate({
                pathname: I.URLPathname.planVersions,
                search: urlSearchParams.toString(),
              });
            }}
            Icon={HistoryIcon}
            title={t('planHistory')}
          />
        )}
        {publishToExternalApi && (
          <AppBarMenuItem onClick={publishPlanHandler} Icon={OutputIcon} title={t('publishToAPI')} />
        )}
        <Divider variant="middle" />
        <AppBarMenuItem onClick={handleSetting} Icon={SettingsIcon} title={t('planSettings')} testid="settings" />
      </Menu>
    </>
  );
};

const AppBarMenuItem = ({
  onClick,
  Icon,
  title,
  testid,
  isDownloading,
  disabled,
}: {
  onClick: (title?: string) => void;
  Icon: OverridableComponent<SvgIconTypeMap> & {muiName: string};
  title: string;
  testid?: string;
  isDownloading?: string;
  disabled?: boolean;
}) => {
  return (
    <MenuItem
      onClick={() => {
        onClick(title);
      }}
      data-testid={testid}
      disabled={disabled}
    >
      <ListItemIcon>
        {isDownloading === title ? <CircularProgress size={20} /> : <Icon fontSize="small" />}
      </ListItemIcon>
      <ListItemText>{title}</ListItemText>
    </MenuItem>
  );
};

const handleClickCopy = (
  planId: string,
  handleClose: () => void,
  inputMenuDispatch?: React.Dispatch<I.InputMenuAction>,
) => {
  inputMenuDispatch &&
    inputMenuDispatch({
      type: inputMenuActionType.setFormData,
      form: FormSelectionEnum.plan,
      method: methodType.copy,
      formData: {planId},
    });
  handleClose();
};

const handleClickEdit = (
  planId: string,
  handleClose: () => void,
  inputMenuDispatch?: React.Dispatch<I.InputMenuAction>,
) => {
  inputMenuDispatch &&
    inputMenuDispatch({
      type: inputMenuActionType.setFormData,
      form: FormSelectionEnum.plan,
      method: methodType.edit,
      formData: {planId},
    });
  handleClose();
};
